import React, { useEffect, useState } from 'react';
import HeaderWithBack from '../../components/HeaderWithBack';
import { useNavigate, useParams } from 'react-router';
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { fetchAPIResponseSelector } from "../../store/user.atom";
import IngredientSelector, { SelectedIngredient } from "../../components/items/IngredientSelector";
import { CategoryDTO } from "../../dtos/category.dto";
import {EnrichedItemDTO} from "../../dtos/item.dto";

const EditItem: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

    const [itemName, setItemName] = useState<string>('');
    const [itemDescription, setItemDescription] = useState<string>('');
    const [itemImage, setItemImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [itemPrice, setItemPrice] = useState<number>();
    const [discountAvailable, setDiscountAvailable] = useState<boolean>(false);
    const [discountPercentage, setDiscountPercentage] = useState<number>(0);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [ingredients, setIngredients] = useState<SelectedIngredient[]>([]);
    const [addons, setAddons] = useState<SelectedIngredient[]>([]);
    const [combos, setCombos] = useState<SelectedIngredient[]>([]);
    const [offerAvailable, setOfferAvailable] = useState<boolean>(false);
    const [disabledItem, setDisabledItem] = useState<boolean>(false);
    const [categories, setCategories] = useState<CategoryDTO[]>([]);

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const itemData = await fetchAPIResponse<EnrichedItemDTO>(`/items/${id}`, {
                    method: "GET",
                    headers: {
                        "Accept": "application/json"
                    }
                }, false);

                const item = itemData.data;
                setItemName(item.name);
                setItemDescription(item.description);
                setImagePreview(item.image);
                setItemPrice(item.base_price);
                setDiscountAvailable(item.discount_options.discount);
                setDiscountPercentage(item.discount_options.discount_percentage);
                setSelectedCategory(item.category);
                setIngredients(item.ingredients.map(r => ({
                    ingredient: {
                        value: r.id,
                        label: r.name,
                    },
                    quantity: r.quantity,
                    minQuantity: r.minQuantity,
                    maxQuantity: r.maxQuantity
                })));
                setAddons(item.addons.map(r => ({
                    ingredient: {
                        value: r.id,
                        label: r.name,
                    },
                    quantity: r.quantity,
                    minQuantity: r.minQuantity,
                    maxQuantity: r.maxQuantity
                })));
                setCombos(item.combos.map(r => ({
                    ingredient: {
                        value: r.id,
                        label: r.name,
                    },
                    quantity: r.quantity,
                    minQuantity: r.minQuantity,
                    maxQuantity: r.maxQuantity
                })));
                setOfferAvailable(item.offer);
                setDisabledItem(item.disabled);
            } catch (error) {
                toast.error((error as Error).message);
            }
        };

        fetchItem();
    }, [id, fetchAPIResponse]);

    useEffect(() => {
        const fetchCategories = async () => {
            const categories = await fetchAPIResponse<CategoryDTO[]>(`/categories?search=`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
            });

            setCategories(categories.data);
        }

        fetchCategories();
    }, [fetchAPIResponse]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setItemImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleDeleteImage = () => {
        setItemImage(null);
        setImagePreview('');
    };

    const handleSaveItem = async () => {
        try {
            const formData = new FormData();
            formData.set("name", itemName);
            formData.set("description", itemDescription);
            if (itemImage) {
                formData.set("image", itemImage, itemImage.name);
            }
            formData.set("base_price", itemPrice?.toFixed(2)!);
            formData.set("category", selectedCategory!);
            formData.set("discount_options", JSON.stringify({
                discount: discountAvailable,
                discount_percentage: discountPercentage,
            }));
            formData.set("ingredients", JSON.stringify(ingredients.map(r => ({
                ingredient: r.ingredient.value,
                quantity: r.quantity,
                minQuantity: r.minQuantity,
                maxQuantity: r.maxQuantity
            }))));
            formData.set("addons", JSON.stringify(addons.map(r => ({
                ingredient: r.ingredient.value,
                quantity: r.quantity,
                minQuantity: r.minQuantity,
                maxQuantity: r.maxQuantity
            }))));
            formData.set("combos", JSON.stringify(combos.map(r => ({
                ingredient: r.ingredient.value,
                quantity: r.quantity,
                minQuantity: r.minQuantity,
                maxQuantity: r.maxQuantity
            }))));
            formData.set("offer", offerAvailable!.toString())
            formData.set("disabled", disabledItem!.toString());

            await fetchAPIResponse<EnrichedItemDTO>(`/items/admin/${id}`, {
                method: "PUT",
                headers: {
                    "Accept": "application/json"
                },
                body: formData
            });

            navigate('/items');
        } catch (e) {
            toast.error((e as Error).message);
        }
    };

    return (
        <div className='flex flex-col w-full h-full overflow-auto items-center py-10 gap-6'>
            <HeaderWithBack previousPage={'Items'} currentPage={'Edit Item'} previousPageUrl={'/items'} />
            <div className='zbh-card w-11/12 max-w-3xl p-8 mb-6'>
                <h2 className="font-display text-base font-semibold text-ink mb-5">Edit item</h2>

                <div className="space-y-5">
                    <div>
                        <label className="zbh-label">Name</label>
                        <input type='text' placeholder='e.g. Turkey Tango Bliss' value={itemName}
                            onChange={(e) => setItemName(e.target.value)} className='zbh-input' />
                    </div>
                    <div>
                        <label className="zbh-label">Description</label>
                        <input type='text' placeholder='Short description shown to customers' value={itemDescription}
                            onChange={(e) => setItemDescription(e.target.value)} className='zbh-input' />
                    </div>

                    <div>
                        <label className="zbh-label">Image</label>
                        <div className='flex items-center gap-4'>
                            <label htmlFor='item-image' className='zbh-btn-ghost cursor-pointer'>Choose image</label>
                            <input type='file' id='item-image' accept='image/*' onChange={handleImageChange} className='hidden' />
                            {imagePreview && (
                                <div className='flex items-center gap-3'>
                                    <img src={imagePreview} alt='Item' className='h-14 w-14 rounded-lg object-cover border border-hair' />
                                    <button onClick={handleDeleteImage} className='zbh-btn-danger px-4 py-1.5 text-xs'>Remove</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label className="zbh-label">Price ($)</label>
                            <input type='number' placeholder='0.00' value={itemPrice}
                                onChange={(e) => setItemPrice(parseFloat(e.target.value))} className='zbh-input' />
                        </div>
                        <div>
                            <label htmlFor='category-select' className="zbh-label">Category</label>
                            <select id='category-select' value={selectedCategory === null ? '' : selectedCategory}
                                onChange={(e) => { setSelectedCategory(e.target.value === '' ? null : e.target.value) }}
                                className='zbh-input'>
                                <option value=''>Select a category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input type='checkbox' checked={discountAvailable}
                            onChange={(e) => setDiscountAvailable(e.target.checked)}
                            className='h-4 w-4 rounded border-hair accent-accent' />
                        <span className="text-sm font-medium text-ink">Discount available</span>
                    </label>
                    {discountAvailable && (
                        <div>
                            <label className="zbh-label">Discount percentage</label>
                            <input type='number' placeholder='e.g. 15' value={discountPercentage}
                                onChange={(e) => setDiscountPercentage(parseFloat(e.target.value))} className='zbh-input' />
                        </div>
                    )}
                </div>

                <div className="zbh-tear my-6" />

                <IngredientSelector type={"Ingredients"} ingredients={ingredients} setIngredients={setIngredients}/>
                <IngredientSelector type={"Addons"} ingredients={addons} setIngredients={setAddons}/>
                <IngredientSelector type={"Combos"} ingredients={combos} setIngredients={setCombos}/>

                <div className="zbh-tear my-6" />

                <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input type='checkbox' checked={offerAvailable}
                            onChange={(e) => setOfferAvailable(e.target.checked)}
                            className='h-4 w-4 rounded border-hair accent-accent' />
                        <span className="text-sm font-medium text-ink">Featured offer</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input type='checkbox' checked={disabledItem}
                            onChange={(e) => setDisabledItem(e.target.checked)}
                            className='h-4 w-4 rounded border-hair accent-accent' />
                        <span className="text-sm font-medium text-ink">Disabled (hidden from menu)</span>
                    </label>
                </div>

                <button onClick={handleSaveItem} className='zbh-btn-accent w-full mt-7'>
                    Save changes
                </button>
            </div>
        </div>
    );
};

export default EditItem;